import { Autocomplete, CircularProgress, TextField } from "@mui/material"
import { useGetUnpackedGraphDataQuery } from "@store/services/graphsService"
import { useImmerAtom } from "jotai-immer"
import { memo } from "react"
import { AutocompleteOnChange } from "@components/NodeLink/types"
import { linkAtom, linkDialogNodeAtom } from "@components/NodeLink/linkAtom"
import { GraphNodeType } from "@custom-types/graphs"
import { isNodeTypeEqual } from "@features/builder/helpers/nodes"
import NodeTypeChip from "@components/NodeTypeChip"
import { useAtom } from "jotai"

type NodeOption = {
  label: string
  type: GraphNodeType
  isLinked: boolean
}
type IndexedNodeOption = NodeOption & { index: number }

const getNodeOptionLabel = ({ label, isLinked }: NodeOption) =>
  isLinked ? `${label} - Can't link to a linked node - use the original node!` : label

const NodeAutocomplete = memo(() => {
  const [linkDialogNode] = useAtom(linkDialogNodeAtom)
  const [link, updateLink] = useImmerAtom(linkAtom)
  const skip = !link.org || !link.div || !link.prod || !link.family || !link.version
  const currentType = linkDialogNode?.type

  const graphKey = `${link.org}#${link.div}#${link.prod}#${link.family}_${link.version}`
  const { data, isFetching } = useGetUnpackedGraphDataQuery(graphKey, {
    skip: skip || !graphKey,
  })

  const nodes: IndexedNodeOption[] = []
  const graphNodes = [...(data?.graph.nodes ?? []), ...(data?.graph.attributes ?? [])]

  for (const [index, node] of Object.values(graphNodes).entries()) {
    if (!isNodeTypeEqual(node.type, currentType)) continue
    if (node.name.includes("__")) continue

    const nodeLabel = data?.meta.nodes[node.name]?.label ?? ""
    const isLinked = Boolean(data?.meta.nodes[node.name]?.Link)

    if (
      data?.meta.nodes[node.name]?.["Scenario Mnem"] ||
      data?.meta.nodes[node.name]?.["Forecast Mnem"]
    )
      continue

    nodes.push({
      index: index,
      label: nodeLabel,
      type: node.type,
      isLinked,
    })
  }

  const sortedOptions = nodes
    .toSorted((nodeA, nodeB) => nodeA.label.localeCompare(nodeB.label))
    .map((node, index) => ({ ...node, index }))

  const handleChange: AutocompleteOnChange<NodeOption | null> = (_event, newValue) => {
    if (newValue?.isLinked) return

    updateLink((draft) => {
      draft.node = newValue?.label ?? ""
    })
  }

  return (
    <Autocomplete
      options={sortedOptions}
      value={nodes.find((n) => n.label === link.node) || null}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.index}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: ".2em",
            opacity: option.isLinked ? 0.5 : 1,
            pointerEvents: option.isLinked ? "none" : "auto",
          }}
        >
          <span>{getNodeOptionLabel(option)}</span>
          <NodeTypeChip type={option.type} />
        </li>
      )}
      renderInput={(params) => <TextField {...params} label="Node" />}
      onChange={handleChange}
      disabled={skip}
      isOptionEqualToValue={(option, value) => option.label === value?.label}
      loading={isFetching}
      loadingText={<CircularProgress size={20} />}
    />
  )
})
NodeAutocomplete.displayName = "NodeAutocomplete"

export default NodeAutocomplete
